import React, { useState, useMemo, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Search,
  Download,
  RefreshCw,
  Plus,
  ChevronDown,
  ChevronRight,
  Filter,
  Layers,
  X,
  Loader2,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import {
  DetailPanel,
  CreateWizard,
  type UnifiedCampaign,
  type UnifiedAdGroup,
  type UnifiedAd,
  type UnifiedAccount,
  type UnifiedRow,
} from "@/components/ads-manager";
import { getSortableValue, type ColDef } from "@/components/ads-manager/CampaignTable";
import { useCanvasStore } from "./canvas-store";

async function fetchJSON<T>(url: string): Promise<T> {
  const res = await fetch(url, { credentials: "include" });
  if (!res.ok) throw new Error(`Failed to fetch: ${res.statusText}`);
  return res.json();
}

const ALWAYS_VISIBLE_COLS: ColDef[] = [
  { key: "platform", label: "Platform", width: "60px" },
  { key: "status", label: "Status", width: "90px" },
  { key: "budget", label: "Budget", align: "right", width: "90px" },
  { key: "spend", label: "Spend", align: "right", sortable: true, width: "90px" },
  { key: "impressions", label: "Impressions", align: "right", sortable: true, width: "100px" },
  { key: "clicks", label: "Clicks", align: "right", sortable: true, width: "75px" },
  { key: "ctr", label: "CTR", align: "right", sortable: true, width: "65px" },
  { key: "conversions", label: "Conv.", align: "right", sortable: true, width: "80px" },
  { key: "roas", label: "ROAS", align: "right", sortable: true, width: "65px" },
];

const META_COLS: ColDef[] = [
  { key: "qualityRanking", label: "Quality", align: "center", width: "70px", metaOnly: true },
  { key: "engagementRanking", label: "Engage Rank", align: "center", width: "90px", metaOnly: true },
  { key: "outboundClicks", label: "Outbound Clicks", align: "right", sortable: true, width: "110px", metaOnly: true },
];

const TIKTOK_COLS: ColDef[] = [
  { key: "completionRate", label: "Compl. Rate", align: "right", sortable: true, width: "90px", tiktokOnly: true },
  { key: "sixSecondViews", label: "6s Views", align: "right", sortable: true, width: "80px", tiktokOnly: true },
  { key: "likes", label: "Likes", align: "right", sortable: true, width: "65px", tiktokOnly: true },
  { key: "shares", label: "Shares", align: "right", sortable: true, width: "65px", tiktokOnly: true },
  { key: "saves", label: "Saves", align: "right", sortable: true, width: "65px", tiktokOnly: true },
  { key: "comments", label: "Comments", align: "right", sortable: true, width: "80px", tiktokOnly: true },
  { key: "profileVisits", label: "Profile Visits", align: "right", sortable: true, width: "95px", tiktokOnly: true },
];

const PRESET_EXTRA: Record<string, ColDef[]> = {
  performance: [
    { key: "cpc", label: "CPC", align: "right", sortable: true, width: "65px" },
    { key: "purchaseValue", label: "Revenue", align: "right", sortable: true, width: "90px" },
    { key: "costPerResult", label: "Cost/Result", align: "right", sortable: true, width: "90px" },
    { key: "videoViews", label: "Video Views", align: "right", sortable: true, width: "100px", tiktokOnly: true },
    ...META_COLS,
    ...TIKTOK_COLS,
  ],
  engagement: [
    { key: "reach", label: "Reach", align: "right", sortable: true, width: "85px" },
    { key: "videoViews", label: "Video Views", align: "right", sortable: true, width: "100px", tiktokOnly: true },
    ...META_COLS,
    ...TIKTOK_COLS,
  ],
  delivery: [
    { key: "reach", label: "Reach", align: "right", sortable: true, width: "85px" },
    { key: "cpm", label: "CPM", align: "right", sortable: true, width: "65px" },
    { key: "costPerResult", label: "Cost/Result", align: "right", sortable: true, width: "90px" },
    { key: "videoViews", label: "Video Views", align: "right", sortable: true, width: "100px", tiktokOnly: true },
    ...META_COLS,
    ...TIKTOK_COLS,
  ],
};

type GroupByOption = "none" | "platform" | "objective" | "status";

const STATUS_COLORS: Record<string, string> = {
  active: "#5E8E6E",
  paused: "#B5B0A8",
  error: "#A67060",
  learning: "#8E7E6E",
  completed: "#B5B0A8",
};

function PlatformLetterBadge({ slug }: { slug: string }) {
  const letter = slug === "meta" ? "M" : slug === "tiktok" ? "T" : slug.charAt(0).toUpperCase();
  return (
    <div
      style={{
        width: 20,
        height: 20,
        borderRadius: 4,
        background: "#E5E2DC",
        color: "#5E5E58",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 10,
        fontWeight: 700,
        flexShrink: 0,
        letterSpacing: 0,
      }}
    >
      {letter}
    </div>
  );
}

function StatusDot({ status }: { status: string }) {
  const color = STATUS_COLORS[status?.toLowerCase()] || "#B5B0A8";
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: color, display: "inline-block", flexShrink: 0 }} />
      <span style={{ color: "#3D3A36", fontSize: 12, textTransform: "capitalize" }}>{status || "—"}</span>
    </span>
  );
}

function ZeroOrValue({ v, format }: { v: number; format: (n: number) => string }) {
  if (v === 0 || v === undefined || v === null) {
    return <span style={{ color: "#B5B0A8" }}>—</span>;
  }
  return <>{format(v)}</>;
}

function getCellValue(row: UnifiedRow, key: string): React.ReactNode {
  const m = row.metrics || ({} as any);
  const fmt = (n: number) => n > 0 ? "$" + n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "—";
  const fmtNum = (n: number) => n > 0 ? n.toLocaleString() : "—";

  switch (key) {
    case "platform": return <PlatformLetterBadge slug={row.platformSlug} />;
    case "status": return <StatusDot status={row.status} />;
    case "budget": {
      const c = row as UnifiedCampaign;
      const b = c.dailyBudget || c.lifetimeBudget || 0;
      return b > 0 ? <ZeroOrValue v={b} format={(n) => "$" + n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} /> : <span style={{ color: "#B5B0A8" }}>—</span>;
    }
    case "spend": return <ZeroOrValue v={m.spend} format={(n) => "$" + n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} />;
    case "impressions": return <ZeroOrValue v={m.impressions} format={(n) => n.toLocaleString()} />;
    case "clicks": return <ZeroOrValue v={m.clicks} format={(n) => n.toLocaleString()} />;
    case "ctr": return <ZeroOrValue v={m.ctr} format={(n) => n.toFixed(2) + "%"} />;
    case "conversions": return <ZeroOrValue v={m.conversions} format={(n) => n.toLocaleString()} />;
    case "roas": return <ZeroOrValue v={m.roas} format={(n) => n.toFixed(2) + "x"} />;
    case "cpc": return <ZeroOrValue v={m.cpc} format={(n) => "$" + n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} />;
    case "cpm": return <ZeroOrValue v={m.cpm} format={(n) => "$" + n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} />;
    case "reach": return <ZeroOrValue v={m.reach} format={(n) => n.toLocaleString()} />;
    case "purchaseValue": return <ZeroOrValue v={m.purchaseValue} format={(n) => "$" + n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} />;
    case "costPerResult": return <ZeroOrValue v={m.costPerResult} format={(n) => "$" + n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} />;
    case "videoViews": return <ZeroOrValue v={m.videoViews} format={(n) => n.toLocaleString()} />;
    case "qualityRanking": return <span style={{ color: "#B5B0A8" }}>—</span>;
    case "engagementRanking": return <span style={{ color: "#B5B0A8" }}>—</span>;
    case "outboundClicks": return <ZeroOrValue v={m.clicks} format={(n) => n.toLocaleString()} />;
    case "completionRate": return m.videoViews ? <>{((m.videoCompletions || 0) / m.videoViews * 100).toFixed(1)}%</> : <span style={{ color: "#B5B0A8" }}>—</span>;
    case "sixSecondViews": return <ZeroOrValue v={m.sixSecondViews} format={(n) => n.toLocaleString()} />;
    case "likes": return <ZeroOrValue v={m.likes} format={(n) => n.toLocaleString()} />;
    case "shares": return <ZeroOrValue v={m.shares} format={(n) => n.toLocaleString()} />;
    case "saves": return <ZeroOrValue v={m.saves} format={(n) => n.toLocaleString()} />;
    case "comments": return <ZeroOrValue v={m.comments} format={(n) => n.toLocaleString()} />;
    case "profileVisits": return <ZeroOrValue v={m.profileVisits} format={(n) => n.toLocaleString()} />;
    default: return <span style={{ color: "#B5B0A8" }}>—</span>;
  }
}

interface CanvasTableRowProps {
  row: UnifiedRow;
  depth: number;
  columns: ColDef[];
  isExpanded?: boolean;
  onToggleExpand?: () => void;
  onDetail: (item: { item: UnifiedRow; type: "campaign" | "adgroup" | "ad" }) => void;
  rowType: "campaign" | "adgroup" | "ad";
  isTotals?: boolean;
}

function CanvasTableRow({
  row,
  depth,
  columns,
  isExpanded,
  onToggleExpand,
  onDetail,
  rowType,
  isTotals,
}: CanvasTableRowProps) {
  const [hovered, setHovered] = useState(false);
  const indent = depth * 20;
  const hasChildren = rowType === "campaign" || rowType === "adgroup";

  return (
    <tr
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => onDetail({ item: row, type: rowType })}
      style={{
        background: hovered ? "#FAFAF8" : "#FFFFFF",
        cursor: "pointer",
        borderBottom: "1px solid #F0EDEA",
        fontWeight: isTotals ? 600 : 400,
      }}
      data-testid={`canvas-ads-row-${rowType}-${row.id}`}
    >
      {/* Name column */}
      <td
        style={{
          padding: "9px 12px",
          fontSize: 13,
          color: "#3D3A36",
          whiteSpace: "nowrap",
          maxWidth: 280,
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 6, paddingLeft: indent }}>
          {hasChildren && onToggleExpand && (
            <button
              onClick={(e) => { e.stopPropagation(); onToggleExpand(); }}
              style={{ background: "none", border: "none", padding: 0, cursor: "pointer", display: "flex", alignItems: "center", color: "#8C8578", flexShrink: 0 }}
              data-testid={`canvas-ads-expand-${rowType}-${row.id}`}
            >
              <ChevronRight style={{ width: 12, height: 12, transform: isExpanded ? "rotate(90deg)" : "none", transition: "transform 150ms" }} />
            </button>
          )}
          {!hasChildren && <span style={{ width: 12, flexShrink: 0, display: "inline-block" }} />}
          <span className="truncate">{row.name}</span>
        </div>
      </td>

      {/* Data columns */}
      {columns.map((col) => (
        <td
          key={col.key}
          style={{
            padding: "9px 12px",
            fontSize: 12.5,
            color: "#3D3A36",
            textAlign: (col.align as any) || "left",
            whiteSpace: "nowrap",
            width: col.width,
            minWidth: col.width,
          }}
        >
          {getCellValue(row, col.key)}
        </td>
      ))}
    </tr>
  );
}

export function CanvasAdsManagerView() {
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState("");
  const [columnPresetKey, setColumnPresetKey] = useState("performance");
  const [sortKey, setSortKey] = useState("spend");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [activePlatforms, setActivePlatforms] = useState<Set<string>>(new Set());
  const [showColumnDropdown, setShowColumnDropdown] = useState(false);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [showGroupDropdown, setShowGroupDropdown] = useState(false);
  const [showAccountDropdown, setShowAccountDropdown] = useState(false);
  const [detailItem, setDetailItem] = useState<{ item: UnifiedRow; type: "campaign" | "adgroup" | "ad" } | null>(null);
  const [showCreateWizard, setShowCreateWizard] = useState(false);
  const [selectedAccountIds, setSelectedAccountIds] = useState<number[]>([]);
  const [expandedCampaigns, setExpandedCampaigns] = useState<Set<number>>(new Set());
  const [expandedAdGroups, setExpandedAdGroups] = useState<Set<number>>(new Set());
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterObjective, setFilterObjective] = useState("all");
  const [filterFormat, setFilterFormat] = useState("all");
  const [groupBy, setGroupBy] = useState<GroupByOption>("none");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest("[data-canvas-dropdown]")) {
        setShowColumnDropdown(false);
        setShowFilterDropdown(false);
        setShowGroupDropdown(false);
        setShowAccountDropdown(false);
      }
    };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, []);

  // Brand-aware endpoint switching: when a brand is active in the canvas,
  // route Ads Manager queries to /api/mock-ads-manager/* (which reads
  // mock_ad_accounts/mock_campaigns/mock_ads tables filtered by brand).
  // Without an active brand, fall back to /api/unified-accounts (global
  // demo data + any synced real Meta accounts).
  const activeBrandId = useCanvasStore((s) => s.activeBrandId);
  const useMockEndpoints = activeBrandId != null;
  const accountsUrl = useMockEndpoints
    ? `/api/mock-ads-manager/accounts?brandId=${activeBrandId}`
    : `/api/unified-accounts`;
  const campaignsBaseUrl = useMockEndpoints ? `/api/mock-ads-manager/campaigns` : `/api/campaigns`;
  const adsetsForCampaignUrl = (cid: number) =>
    useMockEndpoints
      ? `/api/mock-ads-manager/campaigns/${cid}/adsets`
      : `/api/campaigns/${cid}/adsets`;
  const adsForAdsetUrl = (agId: number) =>
    useMockEndpoints
      ? `/api/mock-ads-manager/adsets/${agId}/ads`
      : `/api/adsets/${agId}/ads`;

  const { data: accounts = [] } = useQuery<UnifiedAccount[]>({
    queryKey: [accountsUrl],
    queryFn: () => fetchJSON(accountsUrl),
  });

  const availablePlatforms = useMemo(() => {
    const map = new Map<string, { slug: string; name: string; color: string }>();
    for (const a of accounts) {
      if (!map.has(a.platformSlug)) {
        map.set(a.platformSlug, { slug: a.platformSlug, name: a.platformName, color: a.platformColor });
      }
    }
    return Array.from(map.values());
  }, [accounts]);

  useEffect(() => {
    if (activePlatforms.size === 0 && availablePlatforms.length > 0) {
      setActivePlatforms(new Set(availablePlatforms.map((p) => p.slug)));
    }
  }, [availablePlatforms]);

  const filteredAccountIds = useMemo(() => {
    if (selectedAccountIds.length > 0) return selectedAccountIds;
    return accounts.filter((a) => activePlatforms.has(a.platformSlug)).map((a) => a.id);
  }, [accounts, activePlatforms, selectedAccountIds]);

  const campaignQP = useMemo(() => {
    const p = new URLSearchParams();
    if (filteredAccountIds.length > 0) p.set("accounts", filteredAccountIds.join(","));
    if (searchQuery) p.set("search", searchQuery);
    if (filterStatus !== "all") p.set("status", filterStatus);
    if (filterObjective !== "all") p.set("objective", filterObjective);
    if (dateFrom) p.set("dateFrom", dateFrom);
    if (dateTo) p.set("dateTo", dateTo);
    if (filterFormat !== "all") p.set("format", filterFormat);
    return p.toString();
  }, [filteredAccountIds, searchQuery, filterStatus, filterObjective, dateFrom, dateTo, filterFormat]);

  const { data: campaigns = [], isLoading: campaignsLoading, refetch: refetchCampaigns } = useQuery<UnifiedCampaign[]>({
    queryKey: [campaignsBaseUrl, campaignQP],
    queryFn: () => fetchJSON(`${campaignsBaseUrl}?${campaignQP}`),
    enabled: filteredAccountIds.length > 0,
  });

  const expandedCampaignIds = Array.from(expandedCampaigns);

  const adGroupQueries = useQuery<Record<number, UnifiedAdGroup[]>>({
    queryKey: ["adsets-batch", useMockEndpoints, expandedCampaignIds],
    queryFn: async () => {
      const results: Record<number, UnifiedAdGroup[]> = {};
      await Promise.all(
        expandedCampaignIds.map(async (cid) => {
          results[cid] = await fetchJSON<UnifiedAdGroup[]>(adsetsForCampaignUrl(cid));
        })
      );
      return results;
    },
    enabled: expandedCampaignIds.length > 0,
  });

  const expandedAdGroupIds = Array.from(expandedAdGroups);

  const adQueries = useQuery<Record<number, UnifiedAd[]>>({
    queryKey: ["ads-batch", useMockEndpoints, expandedAdGroupIds],
    queryFn: async () => {
      const results: Record<number, UnifiedAd[]> = {};
      await Promise.all(
        expandedAdGroupIds.map(async (agId) => {
          results[agId] = await fetchJSON<UnifiedAd[]>(adsForAdsetUrl(agId));
        })
      );
      return results;
    },
    enabled: expandedAdGroupIds.length > 0,
  });

  const adGroupsMap = adGroupQueries.data || {};
  const adsMap = adQueries.data || {};

  const handleSort = (key: string) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("desc"); }
  };

  const toggleCampaignExpand = (id: number) => {
    const next = new Set(expandedCampaigns);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setExpandedCampaigns(next);
  };

  const toggleAdGroupExpand = (id: number) => {
    const next = new Set(expandedAdGroups);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setExpandedAdGroups(next);
  };

  const filteredCampaigns = useMemo(() => {
    let items = campaigns.filter((c) => activePlatforms.has(c.platformSlug));
    return [...items].sort((a, b) => {
      const av = getSortableValue(a, sortKey);
      const bv = getSortableValue(b, sortKey);
      return sortDir === "asc" ? av - bv : bv - av;
    });
  }, [campaigns, activePlatforms, sortKey, sortDir]);

  const groupedCampaigns = useMemo<{ label: string; items: UnifiedCampaign[] }[]>(() => {
    if (groupBy === "none") return [{ label: "", items: filteredCampaigns }];
    const groups = new Map<string, UnifiedCampaign[]>();
    for (const c of filteredCampaigns) {
      let key: string;
      if (groupBy === "platform") key = c.platformName;
      else if (groupBy === "objective") key = c.objective || "No Objective";
      else key = c.status;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(c);
    }
    return Array.from(groups.entries()).map(([label, items]) => ({ label, items }));
  }, [filteredCampaigns, groupBy]);

  const columns = useMemo(() => {
    const extra = PRESET_EXTRA[columnPresetKey] || PRESET_EXTRA.performance;
    const all = [...ALWAYS_VISIBLE_COLS, ...extra];
    return all.filter((c) => {
      if (c.tiktokOnly && !activePlatforms.has("tiktok")) return false;
      if (c.metaOnly && !activePlatforms.has("meta")) return false;
      return true;
    });
  }, [columnPresetKey, activePlatforms]);

  const totals = useMemo(() => {
    let spend = 0, conv = 0;
    for (const c of filteredCampaigns) { spend += c.metrics.spend; conv += c.metrics.conversions; }
    return { spend, conversions: conv };
  }, [filteredCampaigns]);

  const objectives = useMemo(() => {
    const set = new Set<string>();
    campaigns.forEach((c) => { if (c.objective) set.add(c.objective); });
    return Array.from(set).sort();
  }, [campaigns]);

  const activeFilterCount = [
    filterStatus !== "all",
    filterObjective !== "all",
    filterFormat !== "all",
    dateFrom !== "",
    dateTo !== "",
  ].filter(Boolean).length;

  const pillBtn = (active = false) => ({
    display: "flex",
    alignItems: "center",
    gap: 5,
    padding: "6px 10px",
    borderRadius: 20,
    border: `1px solid ${active ? "#8C8578" : "#DCDCDC"}`,
    background: "#FFFFFF",
    fontSize: 12,
    color: active ? "#3D3A36" : "#8C8578",
    cursor: "pointer",
    whiteSpace: "nowrap" as const,
  });

  const selectStyle = {
    width: "100%",
    marginTop: 4,
    padding: "6px 8px",
    border: "1px solid #E5E2DC",
    borderRadius: 6,
    fontSize: 12,
    color: "#3D3A36",
    background: "#FFFFFF",
    outline: "none",
  };

  return (
    <div
      style={{ height: "100%", display: "flex", flexDirection: "column", background: "#F0F0EF", fontFamily: "system-ui, sans-serif" }}
      data-testid="canvas-ads-manager-view"
    >
      {/* Header */}
      <div style={{ background: "#FFFFFF", borderBottom: "1px solid #E5E2DC", padding: "14px 20px 10px", flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <h1 style={{ fontSize: 20, fontWeight: 600, color: "#1A1A1A", margin: 0 }} data-testid="canvas-ads-title">
              Ads Manager
            </h1>

            {/* Account selector */}
            <div style={{ position: "relative" }} data-canvas-dropdown>
              <button
                onClick={() => setShowAccountDropdown(!showAccountDropdown)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "5px 10px",
                  borderRadius: 20,
                  border: "1px solid #DCDCDC",
                  background: "#FFFFFF",
                  fontSize: 12,
                  color: "#3D3A36",
                  cursor: "pointer",
                }}
                data-testid="canvas-ads-account-selector"
              >
                {selectedAccountIds.length === 0
                  ? "All Accounts"
                  : `${selectedAccountIds.length} Account${selectedAccountIds.length > 1 ? "s" : ""}`}
                <ChevronDown style={{ width: 12, height: 12, color: "#8C8578" }} />
              </button>
              {showAccountDropdown && (
                <div
                  style={{
                    position: "absolute",
                    top: "calc(100% + 4px)",
                    left: 0,
                    background: "#FFFFFF",
                    border: "1px solid #E5E2DC",
                    borderRadius: 10,
                    boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
                    minWidth: 220,
                    zIndex: 30,
                    padding: "4px 0",
                  }}
                  data-testid="canvas-ads-account-dropdown"
                >
                  <button
                    onClick={() => { setSelectedAccountIds([]); setShowAccountDropdown(false); }}
                    style={{
                      width: "100%",
                      textAlign: "left",
                      padding: "7px 14px",
                      fontSize: 12.5,
                      color: selectedAccountIds.length === 0 ? "#1A1A1A" : "#3D3A36",
                      fontWeight: selectedAccountIds.length === 0 ? 600 : 400,
                      background: "transparent",
                      border: "none",
                      cursor: "pointer",
                    }}
                    onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "#F5F3EE"; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "transparent"; }}
                  >
                    All Accounts
                  </button>
                  {accounts.map((a) => {
                    const sel = selectedAccountIds.includes(a.id);
                    return (
                      <button
                        key={a.id}
                        onClick={() =>
                          setSelectedAccountIds((prev) =>
                            sel ? prev.filter((x) => x !== a.id) : [...prev, a.id]
                          )
                        }
                        style={{
                          width: "100%",
                          textAlign: "left",
                          padding: "7px 14px",
                          fontSize: 12.5,
                          color: sel ? "#1A1A1A" : "#3D3A36",
                          fontWeight: sel ? 600 : 400,
                          background: "transparent",
                          border: "none",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                        onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "#F5F3EE"; }}
                        onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "transparent"; }}
                        data-testid={`canvas-ads-account-${a.id}`}
                      >
                        <PlatformLetterBadge slug={a.platformSlug} />
                        <span>{a.accountName}</span>
                        {a.isDemo && <span style={{ fontSize: 10, color: "#B5B0A8", marginLeft: "auto" }}>demo</span>}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Platform toggles */}
            <div style={{ display: "flex", gap: 4 }}>
              {availablePlatforms.map((p) => {
                const isActive = activePlatforms.has(p.slug);
                return (
                  <button
                    key={p.slug}
                    onClick={() => {
                      const next = new Set(activePlatforms);
                      if (next.has(p.slug) && next.size > 1) next.delete(p.slug);
                      else next.add(p.slug);
                      setActivePlatforms(next);
                    }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                      padding: "3px 8px",
                      borderRadius: 12,
                      border: `1px solid ${isActive ? "#8C8578" : "#E5E2DC"}`,
                      background: isActive ? "#F0EDEA" : "#FFFFFF",
                      fontSize: 11.5,
                      color: isActive ? "#3D3A36" : "#B5B0A8",
                      cursor: "pointer",
                      fontWeight: isActive ? 500 : 400,
                    }}
                    data-testid={`canvas-ads-platform-${p.slug}`}
                  >
                    <PlatformLetterBadge slug={p.slug} />
                    {p.name}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => refetchCampaigns()}
              style={{ padding: 5, borderRadius: 6, background: "transparent", border: "none", cursor: "pointer", color: "#8C8578", display: "flex", alignItems: "center" }}
              data-testid="canvas-ads-refresh"
            >
              <RefreshCw style={{ width: 13, height: 13 }} />
            </button>
          </div>

          {/* Create button */}
          <button
            onClick={() => setShowCreateWizard(true)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "7px 14px",
              borderRadius: 8,
              background: "#1A1A1A",
              color: "#FFFFFF",
              fontSize: 13,
              fontWeight: 500,
              border: "none",
              cursor: "pointer",
            }}
            data-testid="canvas-ads-create"
          >
            <Plus style={{ width: 14, height: 14 }} />
            Create
          </button>
        </div>

        {/* Second row: search + controls */}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {/* Search */}
          <div style={{ position: "relative", flex: 1 }}>
            <Search style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", width: 14, height: 14, color: "#B5B0A8" }} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search campaigns, ad sets, or ads..."
              style={{
                width: "100%",
                paddingLeft: 32,
                paddingRight: 12,
                paddingTop: 7,
                paddingBottom: 7,
                border: "1px solid #DCDCDC",
                borderRadius: 8,
                fontSize: 13,
                color: "#3D3A36",
                background: "#FFFFFF",
                outline: "none",
                boxSizing: "border-box",
              }}
              onFocus={(e) => { (e.currentTarget as HTMLInputElement).style.borderColor = "#8C8578"; }}
              onBlur={(e) => { (e.currentTarget as HTMLInputElement).style.borderColor = "#DCDCDC"; }}
              data-testid="canvas-ads-search"
            />
          </div>

          {/* Filters button */}
          <div style={{ position: "relative" }} data-canvas-dropdown>
            <button
              onClick={() => setShowFilterDropdown(!showFilterDropdown)}
              style={pillBtn(activeFilterCount > 0)}
              data-testid="canvas-ads-filters"
            >
              <Filter style={{ width: 12, height: 12 }} />
              Filters
              {activeFilterCount > 0 && (
                <span
                  style={{
                    background: "#F0EDEA",
                    color: "#5E5E58",
                    borderRadius: 10,
                    padding: "1px 5px",
                    fontSize: 10,
                    fontWeight: 700,
                  }}
                >
                  {activeFilterCount}
                </span>
              )}
            </button>
            {showFilterDropdown && (
              <div
                style={{
                  position: "absolute",
                  right: 0,
                  top: "calc(100% + 4px)",
                  background: "#FFFFFF",
                  border: "1px solid #E5E2DC",
                  borderRadius: 10,
                  boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
                  minWidth: 240,
                  zIndex: 30,
                  padding: 14,
                }}
                data-testid="canvas-ads-filter-dropdown"
              >
                <div style={{ marginBottom: 10 }}>
                  <label style={{ fontSize: 10, fontWeight: 600, color: "#8C8578", textTransform: "uppercase", letterSpacing: "0.06em" }}>Status</label>
                  <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} style={selectStyle} data-testid="canvas-ads-filter-status">
                    <option value="all">All</option>
                    <option value="active">Active</option>
                    <option value="paused">Paused</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>
                <div style={{ marginBottom: 10 }}>
                  <label style={{ fontSize: 10, fontWeight: 600, color: "#8C8578", textTransform: "uppercase", letterSpacing: "0.06em" }}>Objective</label>
                  <select value={filterObjective} onChange={(e) => setFilterObjective(e.target.value)} style={selectStyle} data-testid="canvas-ads-filter-objective">
                    <option value="all">All</option>
                    {objectives.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
                <div style={{ marginBottom: 10 }}>
                  <label style={{ fontSize: 10, fontWeight: 600, color: "#8C8578", textTransform: "uppercase", letterSpacing: "0.06em" }}>Format</label>
                  <select value={filterFormat} onChange={(e) => setFilterFormat(e.target.value)} style={selectStyle} data-testid="canvas-ads-filter-format">
                    <option value="all">All</option>
                    <option value="image">Image</option>
                    <option value="video">Video</option>
                    <option value="carousel">Carousel</option>
                  </select>
                </div>
                <div style={{ marginBottom: 10 }}>
                  <label style={{ fontSize: 10, fontWeight: 600, color: "#8C8578", textTransform: "uppercase", letterSpacing: "0.06em" }}>Date Range</label>
                  <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
                    <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} style={{ ...selectStyle, flex: 1 }} data-testid="canvas-ads-date-from" />
                    <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} style={{ ...selectStyle, flex: 1 }} data-testid="canvas-ads-date-to" />
                  </div>
                </div>
                {activeFilterCount > 0 && (
                  <button
                    onClick={() => { setFilterStatus("all"); setFilterObjective("all"); setFilterFormat("all"); setDateFrom(""); setDateTo(""); }}
                    style={{ fontSize: 12, color: "#A67060", background: "none", border: "none", cursor: "pointer", padding: 0 }}
                    data-testid="canvas-ads-clear-filters"
                  >
                    Clear all filters
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Group by button */}
          <div style={{ position: "relative" }} data-canvas-dropdown>
            <button
              onClick={() => setShowGroupDropdown(!showGroupDropdown)}
              style={pillBtn(groupBy !== "none")}
              data-testid="canvas-ads-group-by"
            >
              <Layers style={{ width: 12, height: 12 }} />
              {groupBy === "none" ? "Group" : `By ${groupBy}`}
            </button>
            {showGroupDropdown && (
              <div
                style={{
                  position: "absolute",
                  right: 0,
                  top: "calc(100% + 4px)",
                  background: "#FFFFFF",
                  border: "1px solid #E5E2DC",
                  borderRadius: 10,
                  boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
                  minWidth: 150,
                  zIndex: 30,
                  padding: "4px 0",
                }}
              >
                {(["none", "platform", "objective", "status"] as GroupByOption[]).map((g) => (
                  <button
                    key={g}
                    onClick={() => { setGroupBy(g); setShowGroupDropdown(false); }}
                    style={{
                      width: "100%",
                      textAlign: "left",
                      padding: "7px 14px",
                      fontSize: 12.5,
                      color: g === groupBy ? "#1A1A1A" : "#3D3A36",
                      fontWeight: g === groupBy ? 600 : 400,
                      background: g === groupBy ? "#F0EDEA" : "transparent",
                      border: "none",
                      cursor: "pointer",
                    }}
                    onMouseEnter={(e) => { if (g !== groupBy) (e.currentTarget as HTMLButtonElement).style.background = "#F5F3EE"; }}
                    onMouseLeave={(e) => { if (g !== groupBy) (e.currentTarget as HTMLButtonElement).style.background = "transparent"; }}
                    data-testid={`canvas-ads-group-${g}`}
                  >
                    {g === "none" ? "No Grouping" : g.charAt(0).toUpperCase() + g.slice(1)}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Column preset button */}
          <div style={{ position: "relative" }} data-canvas-dropdown>
            <button
              onClick={() => setShowColumnDropdown(!showColumnDropdown)}
              style={pillBtn()}
              data-testid="canvas-ads-columns"
            >
              Columns: {columnPresetKey.charAt(0).toUpperCase() + columnPresetKey.slice(1)}
              <ChevronDown style={{ width: 11, height: 11 }} />
            </button>
            {showColumnDropdown && (
              <div
                style={{
                  position: "absolute",
                  right: 0,
                  top: "calc(100% + 4px)",
                  background: "#FFFFFF",
                  border: "1px solid #E5E2DC",
                  borderRadius: 10,
                  boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
                  minWidth: 150,
                  zIndex: 20,
                  padding: "4px 0",
                }}
              >
                {Object.keys(PRESET_EXTRA).map((k) => (
                  <button
                    key={k}
                    onClick={() => { setColumnPresetKey(k); setShowColumnDropdown(false); }}
                    style={{
                      width: "100%",
                      textAlign: "left",
                      padding: "7px 14px",
                      fontSize: 12.5,
                      color: "#FFFFFF",
                      fontWeight: k === columnPresetKey ? 600 : 400,
                      background: k === columnPresetKey ? "#3D3A36" : "transparent",
                      border: "none",
                      cursor: "pointer",
                    }}
                    onMouseEnter={(e) => { if (k !== columnPresetKey) { (e.currentTarget as HTMLButtonElement).style.background = "#F5F3EE"; (e.currentTarget as HTMLButtonElement).style.color = "#3D3A36"; } }}
                    onMouseLeave={(e) => { if (k !== columnPresetKey) { (e.currentTarget as HTMLButtonElement).style.background = "transparent"; (e.currentTarget as HTMLButtonElement).style.color = "#FFFFFF"; } }}
                    data-testid={`canvas-ads-preset-${k}`}
                  >
                    {k.charAt(0).toUpperCase() + k.slice(1)}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Download */}
          <button
            style={{ padding: "7px 10px", borderRadius: 20, border: "1px solid #DCDCDC", background: "#FFFFFF", cursor: "pointer", display: "flex", alignItems: "center" }}
            data-testid="canvas-ads-download"
          >
            <Download style={{ width: 12, height: 12, color: "#8C8578" }} />
          </button>
        </div>

        {/* Active filter pills */}
        {activeFilterCount > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
            {filterStatus !== "all" && (
              <FilterPill label={`Status: ${filterStatus}`} onRemove={() => setFilterStatus("all")} />
            )}
            {filterObjective !== "all" && (
              <FilterPill label={`Objective: ${filterObjective}`} onRemove={() => setFilterObjective("all")} />
            )}
            {filterFormat !== "all" && (
              <FilterPill label={`Format: ${filterFormat}`} onRemove={() => setFilterFormat("all")} />
            )}
            {(dateFrom || dateTo) && (
              <FilterPill label={`Date: ${dateFrom || "start"} – ${dateTo || "end"}`} onRemove={() => { setDateFrom(""); setDateTo(""); }} />
            )}
            <button
              onClick={() => { setFilterStatus("all"); setFilterObjective("all"); setFilterFormat("all"); setDateFrom(""); setDateTo(""); }}
              style={{ fontSize: 11, color: "#A67060", background: "none", border: "none", cursor: "pointer", padding: "2px 4px" }}
            >
              Clear all
            </button>
          </div>
        )}
      </div>

      {/* Table area */}
      <div style={{ flex: 1, overflow: "auto" }}>
        {campaignsLoading ? (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 200, color: "#8C8578", gap: 8 }}>
            <Loader2 style={{ width: 18, height: 18, animation: "spin 1s linear infinite" }} />
            <span style={{ fontSize: 14 }}>Loading campaigns…</span>
          </div>
        ) : (
          <table
            style={{ width: "100%", borderCollapse: "collapse", background: "#FFFFFF" }}
            data-testid="canvas-ads-table"
          >
            <thead>
              <tr style={{ background: "#FAFAF8", borderBottom: "1px solid #E5E2DC" }}>
                <th style={{ padding: "8px 12px", textAlign: "left", fontSize: 11, fontWeight: 600, color: "#8C8578", textTransform: "uppercase", letterSpacing: "0.06em", minWidth: 220 }}>
                  Name
                </th>
                {columns.map((col) => (
                  <th
                    key={col.key}
                    onClick={col.sortable ? () => handleSort(col.key) : undefined}
                    style={{
                      padding: "8px 12px",
                      textAlign: (col.align as any) || "left",
                      fontSize: 11,
                      fontWeight: 600,
                      color: sortKey === col.key ? "#3D3A36" : "#8C8578",
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                      whiteSpace: "nowrap",
                      width: col.width,
                      cursor: col.sortable ? "pointer" : "default",
                      userSelect: "none",
                    }}
                  >
                    {col.label}
                    {col.sortable && sortKey === col.key && (
                      <span style={{ marginLeft: 3, fontSize: 10 }}>{sortDir === "asc" ? "↑" : "↓"}</span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {groupedCampaigns.map(({ label, items }) => (
                <React.Fragment key={label || "__all__"}>
                  {label && (
                    <tr>
                      <td
                        colSpan={columns.length + 1}
                        style={{ padding: "6px 12px", background: "#F5F3EE", fontSize: 11, fontWeight: 600, color: "#8C8578", textTransform: "uppercase", letterSpacing: "0.06em", borderBottom: "1px solid #E5E2DC" }}
                      >
                        {label}
                      </td>
                    </tr>
                  )}
                  {items.map((campaign) => {
                    const isExpanded = expandedCampaigns.has(campaign.id);
                    const adGroups = adGroupsMap[campaign.id] || [];
                    return (
                      <React.Fragment key={`campaign-${campaign.id}`}>
                        <CanvasTableRow
                          row={campaign}
                          depth={0}
                          columns={columns}
                          isExpanded={isExpanded}
                          onToggleExpand={() => toggleCampaignExpand(campaign.id)}
                          onDetail={setDetailItem}
                          rowType="campaign"
                        />
                        {isExpanded && adGroups.map((adGroup) => {
                          const isAGExpanded = expandedAdGroups.has(adGroup.id);
                          const ads = adsMap[adGroup.id] || [];
                          return (
                            <React.Fragment key={`adgroup-${adGroup.id}`}>
                              <CanvasTableRow
                                row={adGroup}
                                depth={1}
                                columns={columns}
                                isExpanded={isAGExpanded}
                                onToggleExpand={() => toggleAdGroupExpand(adGroup.id)}
                                onDetail={setDetailItem}
                                rowType="adgroup"
                              />
                              {isAGExpanded && ads.map((ad) => (
                                <CanvasTableRow
                                  key={`ad-${ad.id}`}
                                  row={ad}
                                  depth={2}
                                  columns={columns}
                                  onDetail={setDetailItem}
                                  rowType="ad"
                                />
                              ))}
                            </React.Fragment>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                </React.Fragment>
              ))}

              {/* Totals row */}
              {filteredCampaigns.length > 0 && (
                <tr style={{ borderTop: "2px solid #E5E2DC", background: "#FAFAF8", fontWeight: 600 }}>
                  <td style={{ padding: "9px 12px", fontSize: 13, color: "#3D3A36" }}>
                    Totals — {filteredCampaigns.length} campaigns
                  </td>
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      style={{ padding: "9px 12px", fontSize: 12.5, color: "#3D3A36", textAlign: (col.align as any) || "left" }}
                    >
                      {col.key === "spend" ? (
                        totals.spend > 0 ? "$" + totals.spend.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : <span style={{ color: "#B5B0A8" }}>—</span>
                      ) : col.key === "conversions" ? (
                        totals.conversions > 0 ? totals.conversions.toLocaleString() : <span style={{ color: "#B5B0A8" }}>—</span>
                      ) : (
                        <span style={{ color: "#B5B0A8" }}>—</span>
                      )}
                    </td>
                  ))}
                </tr>
              )}

              {filteredCampaigns.length === 0 && !campaignsLoading && (
                <tr>
                  <td colSpan={columns.length + 1} style={{ padding: "40px 12px", textAlign: "center", color: "#B5B0A8", fontSize: 13 }}>
                    No campaigns found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Footer */}
      <div style={{ padding: "8px 20px", borderTop: "1px solid #E5E2DC", background: "#FFFFFF", color: "#8C8578", fontSize: 12, flexShrink: 0 }}>
        Results from {filteredCampaigns.length} campaigns
        {activePlatforms.size < availablePlatforms.length && (
          <span> · Filtered by {Array.from(activePlatforms).join(", ")}</span>
        )}
      </div>

      {/* Detail side panel */}
      {detailItem && (
        <div
          style={{
            position: "fixed",
            inset: "0 0 0 auto",
            width: 380,
            background: "#FFFFFF",
            borderLeft: "1px solid #E5E2DC",
            boxShadow: "-4px 0 20px rgba(0,0,0,0.08)",
            zIndex: 50,
            display: "flex",
            flexDirection: "column",
            animation: "slideIn 200ms ease",
          }}
          data-testid="canvas-ads-detail-panel"
        >
          <style>{`
            @keyframes slideIn { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
          `}</style>
          <DetailPanel
            item={detailItem.item}
            itemType={detailItem.type}
            onClose={() => setDetailItem(null)}
          />
        </div>
      )}

      {/* Create wizard */}
      {showCreateWizard && (
        <CreateWizard
          onClose={() => setShowCreateWizard(false)}
          onSave={async (draft) => {
            try {
              const res = await fetch("/api/campaign-drafts", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify(draft),
              });
              if (!res.ok) throw new Error("Failed to save draft");
              toast({ title: "Campaign draft saved", description: `"${draft.name}" saved for ${draft.platforms.join(" & ")} — ready for review.` });
              setShowCreateWizard(false);
            } catch {
              toast({ title: "Error", description: "Failed to save campaign draft. Please try again.", variant: "destructive" });
            }
          }}
        />
      )}
    </div>
  );
}

function FilterPill({ label, onRemove }: { label: string; onRemove: () => void }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        padding: "3px 8px",
        borderRadius: 20,
        background: "#F0EDEA",
        color: "#5E5E58",
        fontSize: 11.5,
      }}
    >
      {label}
      <button
        onClick={onRemove}
        style={{ background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex", alignItems: "center", color: "#8C8578" }}
      >
        <X style={{ width: 10, height: 10 }} />
      </button>
    </span>
  );
}
